import { CanActivateFn, Router } from '@angular/router';
import { inject, Injector } from '@angular/core';
import { StorageContextService } from '../../context/storage-context/storage-context.service';
import {
  FLEX_CHECKOUT_SESSION_ID_KEY,
  LIFETIME_PAYMENT_FAILED_AFTER_REDIRECT_KEY,
  PAYMENT_BACK_URL_KEY,
  PAYMENT_FAILED_AFTER_REDIRECT_KEY,
  PAYMENT_SUCCESS_BACK_URL_KEY,
  PAYMENT_SUCCESS_AFTER_REDIRECT_KEY,
  STRIPE_CHECKOUT_SESSION_ID_KEY,
} from '@payment/const';
import { injectAsync } from '@di-helpers/inject-async';
import { NativeAppService } from '../../core/services/native-app/native-app.servece';
import { PaymentApiService } from '@api/payment-api';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { LessonsContextService } from '../../context/lessons-context/lessons-context.service';

const LIFETIME_PAYWALL_PAGE_PATH = '/payment/lifetime-paywall';

/**
 * Экран возврата покажет ошибку оплаты лайфтайма: страница пейвола или продолжение лендинга
 * (`?continue=`). Модал пейвола возвращает на URL без своего action — маркер там бы протух
 * и позже всплыл на странице пейвола.
 */
function showsLifetimePaymentFailure(backUrl: string): boolean {
  if (!backUrl) return false;
  try {
    const url = new URL(backUrl, window.location.origin);
    return url.pathname === LIFETIME_PAYWALL_PAGE_PATH || url.searchParams.has('continue');
  } catch {
    return false;
  }
}

/** Подтверждает на сервере лайфтайм-чекаут, с которого вернулся пользователь */
async function verifyLifetimeCheckout(
  provider: string | null,
  paymentApi: PaymentApiService,
  storage: StorageContextService
): Promise<boolean> {
  try {
    switch (provider) {
      case 'stripe':
        await lastValueFrom(
          paymentApi.verifyStripeCheckoutSession(storage.getItem(STRIPE_CHECKOUT_SESSION_ID_KEY))
        );
        break;
      case 'flex':
        await lastValueFrom(
          paymentApi.verifyFlexCheckoutSession(storage.getItem(FLEX_CHECKOUT_SESSION_ID_KEY))
        );
        break;
    }
    return true;
  } catch {
    return false;
  }
}

export function PaymentResultGuardFactory(type: 'success' | 'failed', isLifetime = false) {
  const guard: CanActivateFn = async route => {
    const router = inject(Router);
    const storage = inject(StorageContextService);
    const injector = inject(Injector);
    const nativeApp = inject(NativeAppService);
    const paymentApi = inject(PaymentApiService);
    const lessonsContextService = inject(LessonsContextService);

    // Отдельный URL успеха (если чекаут его задал) действует только для подтверждённой оплаты
    const backUrl = (useSuccessBackUrl = false) => {
      const defaultBackUrl = storage.popItem(PAYMENT_BACK_URL_KEY) || '/auth/sign-up/yearly';
      const successBackUrl = useSuccessBackUrl
        ? storage.popItem(PAYMENT_SUCCESS_BACK_URL_KEY)
        : null;

      return router.parseUrl(successBackUrl || defaultBackUrl);
    };
    const markFailed = () => {
      // Общий флаг по-прежнему нужен гвардам лендингов и авторизации
      storage.setItem(PAYMENT_FAILED_AFTER_REDIRECT_KEY, 'true');
      storage.removeItem(PAYMENT_SUCCESS_BACK_URL_KEY);
      // Ошибку оплаты лайфтайма показываем только после его собственного чекаута и только там,
      // куда вернёмся и где её покажут. Back URL читаем без снятия: его снимает возврат ниже.
      if (isLifetime && showsLifetimePaymentFailure(storage.getItem(PAYMENT_BACK_URL_KEY))) {
        storage.setItem(LIFETIME_PAYMENT_FAILED_AFTER_REDIRECT_KEY, 'true');
      }
    };

    if (type === 'success') {
      const subscriptionStore = await injectAsync('SubscriptionStore', injector);
      const userStore = await injectAsync('UserStore', injector);

      const verified =
        !isLifetime ||
        (await verifyLifetimeCheckout(route.paramMap.get('provider'), paymentApi, storage));
      storage.removeItem(STRIPE_CHECKOUT_SESSION_ID_KEY);
      storage.removeItem(FLEX_CHECKOUT_SESSION_ID_KEY);

      if (!verified) {
        // Сервер не подтвердил оплату (напр. 400: сумма не совпала с региональной ценой):
        // доступ не выдаём и возвращаем на экран оплаты с ошибкой, как при отказе чекаута
        markFailed();
        return backUrl();
      }

      if (nativeApp.isInsideNativeApp()) {
        storage.removeItem(PAYMENT_BACK_URL_KEY);
        storage.removeItem(PAYMENT_SUCCESS_BACK_URL_KEY);
        nativeApp.closePopupPaymentSuccess();
        return false;
      }

      await Promise.all([
        subscriptionStore.waitForLoadingSubscriptions(true),
        subscriptionStore.waitForLoadingUpgrades(true),
        userStore.waitForLoading(true),
        firstValueFrom(lessonsContextService.refresh()),
      ]);
      storage.setItem(PAYMENT_SUCCESS_AFTER_REDIRECT_KEY, 'true');
      // TODO: send analytics event or maybe move to payment service
      // await this.analytics.subscribe('stripe', price, this.coupon() ?? undefined);
    } else {
      markFailed();
    }

    return backUrl(type === 'success');
  };

  return guard;
}
